import React, { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

interface RippleEffect {
  id: number;
  x: number;
  y: number;
}

interface MockupCursorProps {
  activeTab: number;
}

// Define cursor path waypoints for each tab
const cursorPaths = [
  // Tab 0: Dashboard - moves around stats and chart
  [
    { x: 200, y: 80, click: true },
    { x: 350, y: 80, click: false },
    { x: 280, y: 160, click: true },
    { x: 180, y: 200, click: false },
  ],
  // Tab 1: AI Workflows - interacts with nodes
  [
    { x: 180, y: 100, click: true },
    { x: 300, y: 120, click: false },
    { x: 400, y: 140, click: true },
    { x: 250, y: 180, click: false },
  ],
  // Tab 2: Social Planner - clicks on calendar
  [
    { x: 220, y: 90, click: true },
    { x: 320, y: 130, click: true },
    { x: 280, y: 170, click: false },
    { x: 380, y: 110, click: true },
  ],
  // Tab 3: Email Campaigns
  [
    { x: 200, y: 100, click: true },
    { x: 350, y: 140, click: false },
    { x: 280, y: 180, click: true },
    { x: 200, y: 200, click: false },
  ],
  // Tab 4: Templates
  [
    { x: 220, y: 100, click: true },
    { x: 340, y: 100, click: true },
    { x: 280, y: 180, click: true },
    { x: 400, y: 140, click: false },
  ],
  // Tab 5: Inbox
  [
    { x: 200, y: 90, click: true },
    { x: 200, y: 130, click: false },
    { x: 200, y: 170, click: true },
    { x: 350, y: 150, click: false },
  ],
];

const MockupCursor: React.FC<MockupCursorProps> = ({ activeTab }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [ripples, setRipples] = useState<RippleEffect[]>([]);
  const [currentWaypoint, setCurrentWaypoint] = useState(0);
  const [position, setPosition] = useState({ x: 200, y: 100 });

  const path = cursorPaths[activeTab] || cursorPaths[0];

  useEffect(() => {
    // Reset on tab change
    setCurrentWaypoint(0);
    setPosition(path[0] || { x: 200, y: 100 });
    setRipples([]);
  }, [activeTab]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWaypoint(prev => {
        const next = (prev + 1) % path.length;
        const waypoint = path[next];
        
        setPosition({ x: waypoint.x, y: waypoint.y });
        
        // Add ripple if this waypoint has a click
        if (waypoint.click) {
          setTimeout(() => {
            const rippleId = Date.now();
            setRipples(prev => [...prev, { id: rippleId, x: waypoint.x, y: waypoint.y }]);
            
            // Remove ripple after animation
            setTimeout(() => {
              setRipples(prev => prev.filter(r => r.id !== rippleId));
            }, 600);
          }, 400); // Delay ripple to sync with cursor arrival
        }
        
        return next;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [path]);

  return (
    <>
      {/* Ripple effects - using transform for GPU acceleration */}
      {ripples.map(ripple => (
        <div
          key={ripple.id}
          className="absolute pointer-events-none z-40 will-change-transform"
          style={{
            transform: `translate3d(${ripple.x + 130 - 16}px, ${ripple.y + 40 - 16}px, 0)`
          }}
        >
          <div 
            className="w-8 h-8 rounded-full animate-[rippleExpand_0.6s_ease-out_forwards]"
            style={{
              backgroundColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.15)'
            }}
          />
        </div>
      ))}

      {/* Cursor - using transform for GPU acceleration to prevent reflows */}
      <div 
        className="absolute top-0 left-0 pointer-events-none z-50 will-change-transform"
        style={{
          transform: `translate3d(${position.x + 130}px, ${position.y + 40}px, 0)`,
          transition: 'transform 0.7s ease-in-out'
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M3 3L13 8L8 9.5L6.5 14L3 3Z" 
            fill={isDark ? '#FFFFFF' : '#111111'}
            stroke={isDark ? '#444444' : '#E0E0E0'}
            strokeWidth="1"
          />
        </svg>
        {/* Click indicator dot */}
        <div 
          className="absolute -bottom-1 -right-1 w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"
        />
      </div>
    </>
  );
};

export default MockupCursor;
